import { Box, Typography, Paper, useTheme, LinearProgress, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getOrgTokens } from "../../theme/style/organizationPalette";

const AssessmentProgressBar = ({ label, subtitle, value, total, color, percent }) => (
    <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{label}</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: "14px", mt: 0.5 }}>{subtitle}</Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                {value.toLocaleString(undefined, { maximumFractionDigits: 1 })} tCO₂e ({percent}%)
            </Typography>
        </Box>
        <LinearProgress
            variant="determinate"
            value={percent}
            sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: "#e2e8f0",
                "& .MuiLinearProgress-bar": { bgcolor: color }
            }}
        />
    </Box>
);

const AIInsightCard = ({ type, title, description }) => {
    const dotColor = {
        critical: "#f43f5e", // red
        warning: "#f59e0b", // orange
        info: "#10b981",    // green
    }[type];

    return (
        <Box
            sx={{
                p: 2,
                mb: 2,
                borderRadius: "16px",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                transition: "all 0.3s ease",
                "&:hover": { transform: "translateX(8px)", borderColor: dotColor }
            }}
        >
            <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: dotColor, mt: 1, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: "left" }}>{title}</Typography>
                <Typography variant="body2" sx={{ color: "textSecondary", mt: 0.5, fontSize: "14px", textAlign: "left", lineHeight: 1.6 }}>{description}</Typography>
            </Box>
        </Box>
    );
};

const OverallIntelligence = ({ data = [], organizations = [] }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const tok = getOrgTokens(isDark);

    // ── Actual Data Aggregation ────────────────────────────────
    const totalScope1_kg = data.reduce((sum, item) => sum + (Number(item.scope_1) || 0), 0);
    const totalScope2_kg = data.reduce((sum, item) => sum + (Number(item.scope_2) || 0), 0);
    const totalScope3_kg = data.reduce((sum, item) => sum + (Number(item.scope_3) || 0), 0);
    const portfolioTotal_kg = totalScope1_kg + totalScope2_kg + totalScope3_kg;

    const averageGWP_kg = data.length > 0 ? portfolioTotal_kg / data.length : 0;

    // Find biggest outlier
    const outlier = data.length > 1
        ? [...data].sort((a, b) => (Number(b.gwp) || 0) - (Number(a.gwp) || 0))[0]
        : null;

    const outlierMagnitude = outlier && averageGWP_kg > 0
        ? ((Number(outlier.gwp) / averageGWP_kg) * 100).toFixed(0)
        : 0;

    const s1Percent = portfolioTotal_kg > 0 ? Math.round((totalScope1_kg / portfolioTotal_kg) * 100) : 0;
    const s2Percent = portfolioTotal_kg > 0 ? Math.round((totalScope2_kg / portfolioTotal_kg) * 100) : 0;
    const s3Percent = portfolioTotal_kg > 0 ? Math.round((totalScope3_kg / portfolioTotal_kg) * 100) : 0;

    // Convert to Tonnes for display
    const portfolioTotal = portfolioTotal_kg / 1000;

    // Derived Assessment Segments
    const assessmentData = [
        {
            label: "Scope 1 - Direct",
            subtitle: "Direct emissions from owned/controlled sources",
            value: totalScope1_kg / 1000,
            percent: s1Percent,
            color: "#22c55e"
        },
        {
            label: "Scope 2 - Energy Indirect",
            subtitle: "Purchased electricity, steam, heating/cooling",
            value: totalScope2_kg / 1000,
            percent: s2Percent,
            color: "#3b82f6"
        },
        {
            label: "Scope 3 - Other Indirect",
            subtitle: "All other indirect emissions in value chain",
            value: totalScope3_kg / 1000,
            percent: s3Percent,
            color: "#f97316"
        },
    ];

    // Dynamic AI Insights based on real directory state
    const aiInsights = [
        {
            type: "critical",
            title: "Supply Chain Exposure",
            description: `Based on your aggregate data, ${s3Percent}% of total emissions are Scope 3. We recommend focusing on supplier engagement for high-impact entities.`
        },
        {
            type: outlierMagnitude > 150 ? "critical" : "warning",
            title: outlier ? `${outlier.name || 'Top Entity'} Outlier` : "Operational Outlier",
            description: outlier
                ? `${outlier.name} exceeds the portfolio average by ${outlierMagnitude}%. This indicates a significant opportunity for localized decarbonization.`
                : "No significant outliers detected in the current entity set."
        },
        {
            type: "info",
            title: "Data Quality Status",
            description: `Analyzing ${organizations.length} active entities. GHG Protocol alignment is confirmed for the FY 2024 reporting period.`
        },
    ];

    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, animation: "org-fadeInUp 0.4s ease" }}>
            {/* AI Insights Card */}
            <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 400 }}>
                <Paper
                    variant="Standard Container"
                    sx={{ p: 2.5, height: "100%", borderRadius: "24px", border: "1px solid", borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#e2e8f0', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'white' }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
                        <Box sx={{ p: 1, borderRadius: "10px", bgcolor: "#ecfdf5", color: "#10b981", display: "flex" }}>
                            <Typography sx={{ fontSize: "20px" }}>💡</Typography>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 500, pb: 1, fontSize: "20px", color: tok.text.chartTitle, borderBottom: `1px solid ${tok.border.divider}` }}>Intelligence Insights</Typography>
                    </Box>

                    <Box>
                        {aiInsights.map((insight, idx) => (
                            <AIInsightCard key={idx} {...insight} />
                        ))}
                    </Box>
                </Paper>
            </Box>

            {/* GHG Assessment Card */}
            <Box sx={{ flex: "1 1 calc(50% - 12px)", minWidth: 400 }}>
                <Paper
                    variant="Standard Container"
                    sx={{ p: 2.5, height: "100%", borderRadius: "24px", border: "1px solid", borderColor: theme.palette.divider, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'white' }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 500, pb: 1, mb: 4, fontSize: "20px", color: tok.text.chartTitle, borderBottom: `1px solid ${tok.border.divider}` }}>GHG Emission Assessment</Typography>

                    <Box sx={{ mb: 4 }}>
                        {assessmentData.map((item, idx) => (
                            <AssessmentProgressBar key={idx} {...item} total={portfolioTotal} />
                        ))}
                    </Box>

                    <Paper variant="Table Container" sx={{ p: 0, overflow: "hidden", border: "1px solid", borderColor: theme.palette.mode === 'dark' ? "#3f3f46" : "#e2e8f0", borderRadius: "24px", bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff' }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                    Total Portfolio: {portfolioTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })} tCO₂e
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ fontSize: "14px", mt: 0.5 }}>
                                    Assessment Period: FY 2024 • Global GHG Standard
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Paper>
            </Box>
        </Box>
    );
};

export default OverallIntelligence;
